import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { HttpClientError, IDefaultResponseMessage, IQuerySettings, QueryKeys, getQuerySettings } from '@dw/models/shared';
import { ISaveNoticeThemeRequest, ISchoolNoticeTheme, IUpdateNoticeThemeRequest } from '@dw/models/notice';
import { useHttpClient, useSnackbar } from '@dw/hooks';

const BASE_URL = '/v1/dance-schools/notice-themes';

/**
 * Every notice theme in the school's palette.
 *
 * A school that has never edited its palette gets the seven built-ins back from the API as if they
 * were stored, so nothing here needs to know whether a school has customised anything — the list is
 * simply never empty.
 */
export const useGetNoticeThemes = (settings?: IQuerySettings<ISchoolNoticeTheme[]>) => {
  const { GET } = useHttpClient();

  return useQuery<ISchoolNoticeTheme[], HttpClientError>({
    queryKey: [QueryKeys.NoticeThemes],
    queryFn: () => GET<ISchoolNoticeTheme[]>(BASE_URL),
    ...getQuerySettings(settings)
  });
};

/**
 * Every theme mutation invalidates the NOTICES list as well as the palette.
 *
 * Not belt-and-braces: each notice carries its own resolved colours, so recolouring a theme changes
 * every notice already pinned on it. Refreshing only the palette would leave the board showing the
 * old colour until something else happened to refetch it.
 */
const useInvalidateThemesAndBoard = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: [QueryKeys.NoticeThemes] });
    queryClient.invalidateQueries({ queryKey: [QueryKeys.Notices] });
  };
};

export const useCreateNoticeThemeMutation = (settings?: IQuerySettings<ISchoolNoticeTheme, ISaveNoticeThemeRequest>) => {
  const { POST } = useHttpClient();
  const { showSnackbar } = useSnackbar();
  const invalidate = useInvalidateThemesAndBoard();

  const querySettings = getQuerySettings(settings);

  return useMutation<ISchoolNoticeTheme, HttpClientError, ISaveNoticeThemeRequest>({
    mutationFn: (request) => POST<ISchoolNoticeTheme>(BASE_URL, request),
    ...querySettings,
    onSuccess: (response, input) => {
      invalidate();
      showSnackbar('Theme added');

      querySettings.onSuccess?.(response, input);
    }
  });
};

export const useUpdateNoticeThemeMutation = (settings?: IQuerySettings<ISchoolNoticeTheme, IUpdateNoticeThemeRequest>) => {
  const { PUT } = useHttpClient();
  const { showSnackbar } = useSnackbar();
  const invalidate = useInvalidateThemesAndBoard();

  const querySettings = getQuerySettings(settings);

  return useMutation<ISchoolNoticeTheme, HttpClientError, IUpdateNoticeThemeRequest>({
    mutationFn: ({ noticeThemeId, ...request }) => PUT<ISchoolNoticeTheme>(`${BASE_URL}/${noticeThemeId}`, request),
    ...querySettings,
    onSuccess: (response, input) => {
      invalidate();
      showSnackbar('Theme saved');

      querySettings.onSuccess?.(response, input);
    }
  });
};

export const useDeleteNoticeThemeMutation = (settings?: IQuerySettings<IDefaultResponseMessage, string>) => {
  const { DELETE } = useHttpClient();
  const { showSnackbar } = useSnackbar();
  const invalidate = useInvalidateThemesAndBoard();

  const querySettings = getQuerySettings(settings);

  return useMutation<IDefaultResponseMessage, HttpClientError, string>({
    mutationFn: (noticeThemeId) => DELETE<IDefaultResponseMessage>(`${BASE_URL}/${noticeThemeId}`),
    ...querySettings,
    onSuccess: (response, input) => {
      invalidate();
      showSnackbar('Theme deleted');

      querySettings.onSuccess?.(response, input);
    }
  });
};
